import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { createPortal } from "react-dom";
import { ReactComponent as CloseIcon } from "../../Assets/images/closeIcon.svg";
import { getVoiceCoachingHistory } from "../../Services/Chat";

function VoiceChatHistory({ historyMenuClose, onHistorySessionClick }) {
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // initial fetch
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getVoiceCoachingHistory(30, 0, true, true)
      .then((res) => {
        if (!mounted) return;
        // Voice API returns grouped content under res.meeting_notes
        const grouped = res?.meeting_notes || {};
        setGroupedData(grouped);
        // If backend provides count/skip, use it; else disable pagination
        const total = res?.count ?? 0;
        if (total > 0) {
          setHasMore(30 < total);
          setSkip(30);
        } else {
          setHasMore(false);
        }
      })
      .catch((e) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // infinite scroll
  useEffect(() => {
    const container = document.querySelector('.chat-history-list-cntr');
    if (!container) return;
    const onScroll = () => {
      const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
      if (nearBottom && !loadingMore && hasMore) loadMore();
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingMore]);

  const loadMore = () => {
    setLoadingMore(true);
    getVoiceCoachingHistory(30, skip, true, true)
      .then((res) => {
        const nextData = res?.meeting_notes || {};
        setGroupedData((prev) => {
          const merged = { ...(prev || {}) };
          Object.entries(nextData).forEach(([k, arr]) => {
            const existing = merged[k] || [];
            const seen = new Set(existing.map((it) => it.coaching_id || it.id));
            const appended = (arr || []).filter((it) => !seen.has(it.coaching_id || it.id));
            merged[k] = [...existing, ...appended];
          });
          return merged;
        });
        const total = res?.count ?? 0;
        const newSkip = skip + 30;
        setSkip(newSkip);
        setHasMore(total ? newSkip < total : false);
      })
      .finally(() => setLoadingMore(false));
  };

  // search only client-side by title/topic/description
  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groupedData;
    const out = {};
    Object.entries(groupedData || {}).forEach(([k, arr]) => {
      out[k] = (arr || []).filter((it) => {
        const t = (it?.topic || it?.title || "").toLowerCase();
        const d = (it?.description || "").toLowerCase();
        return t.includes(q) || d.includes(q);
      });
    });
    return out;
  }, [groupedData, search]);

  // Flatten, sort by timestamp desc, and take last 5 conversations
  const topFiveItems = useMemo(() => {
    const all = [];
    Object.values(filteredData || {}).forEach((arr) => {
      (arr || []).forEach((it) => all.push(it));
    });
    const sorted = all.sort((a, b) => {
      const ta = new Date(a?.timestamp || a?.created_at || a?.date || 0).getTime();
      const tb = new Date(b?.timestamp || b?.created_at || b?.date || 0).getTime();
      return tb - ta;
    });
    return sorted.slice(0, 5);
  }, [filteredData]);

  return (
    <div className="chat-history-container">
      <div className="chat-history-top-header">
        <div className="chat-history-header cur-ptr" onClick={historyMenuClose}>
          <CloseIcon />
        </div>
        <div className="chat-heading">Aris Dialogues history</div>
        {/* <div className="chat-history-search">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="chat-history-search-input"
          />
          <span className="chat-history-search-icon" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 21L17.65 17.65M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#7E7F83" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div> */}
      </div>
      <div className="chat-history-list-cntr">
        {loading && <div className="chat-history-loading">Loading...</div>}
        {error && <div className="chat-history-error">{String(error)}</div>}
        {!loading && !error && (
          <>
            {topFiveItems.length === 0 && (
              <div className="chat-history-empty" style={{ padding: "16px", color: "#7E7F83" }}>
                No voice dialogues present. Please start voice dialgoue to see your session summary.
              </div>
            )}
            {topFiveItems.map((item, idx) => (
              <VoiceHistoryItem
                key={`${item.coaching_id || item.timestamp || idx}`}
                item={item}
                onClick={() => { window.location.href = "/coachingHistory"; }}
              />
            ))}
            {loadingMore && <div className="chat-history-loading">Loading more…</div>}
          </>
        )}
      </div>
      <div className="chat-history-footer" style={{ padding: "12px 16px", borderTop: "1px solid #E6E7EB" }}>
        <button
          type="button"
          className="chat-history-all-link"
          onClick={() => { window.location.href = "/coachingHistory?tab=dialogues"; }}
          style={{
            background: "transparent",
            border: "none",
            color: "#0B6BFF",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 600,
            padding: 0,
          }}
        >
          All Session History
        </button>
      </div>
    </div>
  );
}

export function VoiceHistoryItem({ item, onClick }) {
  const [expanded, setExpanded] = useState(false);
  const title = item?.topic || item?.title || "Untitled";
  // Format the API's `timestamp` like: Aug 25, 2025 12:56 PM
  const subtitleRaw = item?.timestamp || item?.date || item?.created_at || item?.subtitle;
  const subtitle = useMemo(() => {
    if (!subtitleRaw) return "";
    try {
      const d = new Date(subtitleRaw);
      // e.g., Aug 25, 2025 12:56 PM
      return d.toLocaleString(undefined, {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return String(subtitleRaw);
    }
  }, [subtitleRaw]);
  const body = item?.description || "";

  const normalizedBody = useMemo(() => {
    // Normalize literal '/n' to real newlines and cleanup
    let s = String(body ?? "");
    s = s.replaceAll("/n", "\n");
    // Also convert escaped "\\n" to actual newline
    s = s.replace(/\\n/g, "\n");
    // Normalize CRLF to LF
    s = s.replace(/\r\n?/g, "\n");
    return s.trim();
  }, [body]);

  const TRUNCATE = 260; // show a bit more text per design
  const displayText = expanded ? normalizedBody : (normalizedBody || "").slice(0, TRUNCATE);

  return (
    <div className="chat-history-item voice-history-item" onClick={onClick}>
      <div className="chat-history-item-left">
        <span className="chat-history-item-text">
          <div className="voice-item-title">{title}</div>
          {subtitle && <div className="voice-item-subtitle">{subtitle}</div>}
          {normalizedBody && (
            <div className="voice-item-body" style={{ color: "#1B2430", alignItems: "left" }}>
              <ReactMarkdown
                components={{
                  p: ({ node, ...props }) => (
                    <p style={{ textAlign: "left", color: "#1B2430" }} {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li style={{ textAlign: "left", color: "#1B2430" }} {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a style={{ textAlign: "left", color: "#0B6BFF" }} {...props} />
                  ),
                }}
              >
                {displayText}
              </ReactMarkdown>
              {normalizedBody.length > TRUNCATE && (
                <span
                  className="voice-item-toggle"
                  style={{ color: "#0B6BFF", cursor: "pointer", fontWeight: 600 }}
                  onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
                >
                  {expanded ? " Show Less" : " Show More"}
                </span>
              )}
            </div>
          )}
        </span>
      </div>
    </div>
  );
}

export default VoiceChatHistory;
